import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Chip as MuiChip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Settings as SettingsIcon,
  Image as ImageIcon,
  PlayArrow as PlayIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Schedule as TimeIcon,
  FormatListBulleted as FormatIcon,
  Description as PromptIcon,
  CheckCircle as CompletedIcon,
  Error as ErrorIcon,
  VideoFile as VideoIcon,
} from '@mui/icons-material';
import { usePermissions } from '../../contexts/PermissionContext';
import { API_URLS } from '../../config/apiConfig';
import { getAdminAITestPreviewUrl } from '../../utils/adminAITestImageUtils';
import SecureImage from '../Common/SecureImage';
import SecureVideo from '../Common/SecureVideo';

// Helper function to get secure admin AI test image URL
const getSecureAdminAITestUrl = (imagePath, generationId, isInputImage = false) => {
  if (!imagePath) return null;
  
  // If it's already a full URL (starts with http), return as-is
  if (imagePath.startsWith('http')) {
    return imagePath;
  }
  
  // For output images (generated by admin AI test), use the secure preview API
  if (!isInputImage && generationId) {
    return getAdminAITestPreviewUrl(generationId);
  }
  
  // For input images or fallback, use the general uploads route
  if (imagePath.startsWith('uploads/')) {
    return `${API_URLS.BACKEND_BASE}/api/merchant/ai-generation/uploads/${imagePath}`;
  }
  
  // For other relative paths, assume they're in uploads
  return `${API_URLS.BACKEND_BASE}/api/merchant/ai-generation/uploads/${imagePath}`;
};

const TestModule = () => {
  const { hasPermission, menu } = usePermissions();
  const location = useLocation();

  // Find which module points to this route
  const currentRoute = location.pathname.slice(1); // Remove leading slash
  const moduleInfo = menu.find(item => item.route === currentRoute);
  
  // Use the module name from the access control system, or fallback to 'Test'
  const moduleName = moduleInfo ? moduleInfo.module : 'Test';

  // State for AI test history
  const [activeTab, setActiveTab] = useState(0);
  const [aiHistory, setAiHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(null);

  const canView = hasPermission(moduleName, 'view');
  const canCreate = hasPermission(moduleName, 'create');
  const canUpdate = hasPermission(moduleName, 'update');
  const canDelete = hasPermission(moduleName, 'delete');
  const canExport = hasPermission(moduleName, 'export');
  const canImport = hasPermission(moduleName, 'import');

  // API function to fetch AI test history
  const fetchAiHistory = async () => {
    if (!canView) return;
    
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URLS.ADMIN_BASE}/ai-test/history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('AI History API Response:', data); // Debug log
      setAiHistory(data.history || []);
    } catch (err) {
      setError(`Failed to load AI test history: ${err.message}`);
      console.error('Error fetching AI history:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load AI history when component mounts or tab changes
  useEffect(() => {
    if (activeTab === 1) { // History tab
      fetchAiHistory();
    }
  }, [activeTab, canView]);

  // Auto-refresh for processing items
  useEffect(() => {
    if (activeTab === 1 && aiHistory.length > 0) {
      const hasProcessingItems = aiHistory.some(item => 
        item.status === 'PROCESSING' || item.status === 'PENDING'
      );
      
      if (hasProcessingItems) {
        // Set up auto-refresh every 3 seconds for processing items
        const interval = setInterval(() => {
          fetchAiHistory();
        }, 3000);
        setRefreshInterval(interval);
        
        return () => {
          clearInterval(interval);
          setRefreshInterval(null);
        };
      } else {
        // Clear interval if no processing items
        if (refreshInterval) {
          clearInterval(refreshInterval);
          setRefreshInterval(null);
        }
      }
    }
  }, [activeTab, aiHistory, refreshInterval]);

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [refreshInterval]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle image click
  const handleImageClick = (imageUrl, title) => {
    setSelectedImage({ url: imageUrl, title });
    setImageDialogOpen(true);
  };

  // Close image dialog
  const handleCloseImageDialog = () => {
    setImageDialogOpen(false);
    setSelectedImage(null);
  };

  // Handle view details
  const handleViewDetails = (item) => {
    setSelectedHistoryItem(item);
    setDetailsDialogOpen(true);
  };

  // Close details dialog
  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedHistoryItem(null);
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED': return 'success';
      case 'FAILED': return 'error';
      case 'PROCESSING': return 'warning';
      case 'PENDING': return 'default';
      default: return 'default';
    }
  };

  if (!canView) {
    return (
      <Box p={3}>
        <Alert severity="error">
          You don't have permission to view {moduleName} module.
        </Alert>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Current route: {location.pathname}<br/>
          Detected module: {moduleName}<br/>
          Required permission: {moduleName}.view
        </Typography>
      </Box>
    );
  }

  const permissions = [
    { action: 'View', allowed: canView, description: `Can view ${moduleName} module data` },
    { action: 'Create', allowed: canCreate, description: 'Can create new records' },
    { action: 'Update', allowed: canUpdate, description: 'Can edit existing records' },
    { action: 'Delete', allowed: canDelete, description: 'Can delete records' },
    { action: 'Export', allowed: canExport, description: 'Can export data' },
    { action: 'Import', allowed: canImport, description: 'Can import data' },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {moduleName}
      </Typography>

      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="body1">
          🎉 <strong>Success!</strong> {moduleName} module is working with dynamic routing!
        </Typography>
      </Alert>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Module Info" />
          <Tab label="AI Test History" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  Module Information
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                This component automatically detects which access control module points to this route
                and uses the correct permissions.
              </Typography>
              
              <Typography variant="body2" color="text.secondary">
                <strong>Module Name:</strong> {moduleName}<br/>
                <strong>Current Route:</strong> {location.pathname}<br/>
                <strong>Detected Route:</strong> {currentRoute}<br/>
                <strong>Component:</strong> TestModule (hardcoded)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Your Permissions
              </Typography>
              
              <Box>
                {permissions.map((perm) => (
                  <Box key={perm.action} display="flex" alignItems="center" mb={1}>
                    <Chip
                      icon={perm.allowed ? <CheckCircleIcon /> : undefined}
                      label={perm.action}
                      color={perm.allowed ? 'success' : 'default'}
                      variant={perm.allowed ? 'filled' : 'outlined'}
                      size="small"
                      sx={{ minWidth: 80, mr: 2 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {perm.description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Next Steps
              </Typography>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                Now that TestModule is working, you can:
              </Typography>
              
              <ul>
                <li>
                  <Typography variant="body2">
                    <strong>Test Permission Changes:</strong> Go to Permissions → Role Permissions, 
                    remove the 'view' permission from TestModule, and see it disappear from the sidebar.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>Add More Modules:</strong> Create additional modules in the Modules section 
                    and they will automatically appear here.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>Create Custom Actions:</strong> Use the "Manage Actions" feature in Modules 
                    to add custom actions beyond the standard CRUD operations.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>User-Specific Permissions:</strong> Grant specific permissions to individual 
                    users using the User Permissions tab.
                  </Typography>
                </li>
              </ul>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {/* AI Test History Tab */}
      {activeTab === 1 && (
        <Box>
          {/* Header Section */}
          <Box 
            display="flex" 
            justifyContent="space-between" 
            alignItems="center" 
            mb={3}
            sx={{
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 0 }
            }}
          >
            <Box>
              <Typography variant="h4" component="h2" gutterBottom>
                AI Test Generation History
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and manage your AI test generation results
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              {refreshInterval && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: 'success.main',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%': {
                          opacity: 1,
                          transform: 'scale(1)'
                        },
                        '50%': {
                          opacity: 0.5,
                          transform: 'scale(1.2)'
                        },
                        '100%': {
                          opacity: 1,
                          transform: 'scale(1)'
                        }
                      }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Auto-refreshing
                  </Typography>
                </Box>
              )}
              <Button
                startIcon={<RefreshIcon />}
                onClick={fetchAiHistory}
                disabled={loading}
                variant="outlined"
                size="medium"
                sx={{
                  minWidth: 120,
                  height: 40
                }}
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </Button>
            </Box>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2
              }}
            >
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading ? (
            <Box 
              display="flex" 
              flexDirection="column"
              justifyContent="center" 
              alignItems="center"
              p={6}
            >
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Loading AI test history...
              </Typography>
            </Box>
          ) : aiHistory.length === 0 ? (
            /* Empty State */
            <Card 
              sx={{ 
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }}
            >
              <CardContent 
                sx={{ 
                  textAlign: 'center', 
                  py: 8,
                  px: 4
                }}
              >
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    backgroundColor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3
                  }}
                >
                  <ImageIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                </Box>
                <Typography variant="h6" gutterBottom color="text.secondary">
                  No AI Test Generations Found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Use the AI Test Module to create some test generations and see them here.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<SettingsIcon />}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1
                  }}
                >
                  Go to AI Test Module
                </Button>
              </CardContent>
            </Card>
          ) : (
            /* Results Grid */
            <Grid container spacing={3}>
              {aiHistory.map((item) => (
                <Grid item xs={12} sm={6} lg={4} xl={3} key={item.id}>
                  <Card 
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      {/* Header */}
                      <Box 
                        display="flex" 
                        justifyContent="space-between" 
                        alignItems="flex-start" 
                        mb={2}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography 
                            variant="h6" 
                            noWrap
                            sx={{ 
                              fontWeight: 600,
                              color: 'text.primary',
                              mb: 0.5
                            }}
                          >
                            {item.batch_id || `Generation #${item.id}`}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            ID: {item.id}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <MuiChip
                            label={item.status}
                            color={getStatusColor(item.status)}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              fontSize: '0.75rem'
                            }}
                          />
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(item)}
                              color="primary"
                              sx={{
                                backgroundColor: 'primary.50',
                                '&:hover': {
                                  backgroundColor: 'primary.100'
                                }
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      {/* Metadata */}
                      <Box 
                        display="flex" 
                        flexWrap="wrap" 
                        gap={1} 
                        mb={2}
                        sx={{
                          '& > *': {
                            fontSize: '0.75rem'
                          }
                        }}
                      >
                        <Chip
                          icon={<FormatIcon />}
                          label={item.output_format?.toUpperCase() || 'UNKNOWN'}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                        {item.processing_time_seconds && (
                          <Chip
                            icon={<TimeIcon />}
                            label={`${item.processing_time_seconds}s`}
                            size="small"
                            variant="outlined"
                            color="secondary"
                          />
                        )}
                      </Box>

                      {/* Prompt Preview */}
                      {item.prompt && (
                        <Box mb={2}>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              lineHeight: 1.4,
                              fontSize: '0.875rem'
                            }}
                          >
                            <strong>Prompt:</strong> {item.prompt}
                          </Typography>
                        </Box>
                      )}

                      {/* Images Section */}
                      <Box sx={{ mb: 2 }}>
                        {/* Input Image */}
                        {item.original_image_path && (
                          <Box mb={2}>
                            <Typography 
                              variant="subtitle2" 
                              gutterBottom
                              sx={{ 
                                fontWeight: 600,
                                color: 'text.primary',
                                fontSize: '0.875rem'
                              }}
                            >
                              Input Image
                            </Typography>
                            <Box
                              sx={{
                                width: '100%',
                                height: 120,
                                border: '2px solid',
                                borderColor: 'grey.200',
                                borderRadius: 2,
                                overflow: 'hidden',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'grey.50',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  borderColor: 'primary.main',
                                  backgroundColor: 'primary.50'
                                }
                              }}
                              onClick={() => handleImageClick(item.original_image_path, 'Input Image')}
                            >
                              <img
                                src={item.original_image_path}
                                alt="Input"
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  objectFit: 'cover',
                                }}
                              />
                            </Box>
                          </Box>
                        )}

                        {/* Output Image/Video */}
                        {(item.generated_image_path || item.generated_video_path) && (
                          <Box>
                            <Typography 
                              variant="subtitle2" 
                              gutterBottom
                              sx={{ 
                                fontWeight: 600,
                                color: 'text.primary',
                                fontSize: '0.875rem'
                              }}
                            >
                              Output {item.output_format === 'video' ? 'Video' : 'Image'}
                            </Typography>
                            <Box
                              sx={{
                                width: '100%',
                                height: 120,
                                border: '2px solid',
                                borderColor: 'success.200',
                                borderRadius: 2,
                                overflow: 'hidden',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'success.50',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  borderColor: 'success.main',
                                  backgroundColor: 'success.100'
                                }
                              }}
                              onClick={() => handleImageClick(
                                getSecureAdminAITestUrl(item.generated_image_path || item.generated_video_path, item.id, false),
                                `Output ${item.output_format === 'video' ? 'Video' : 'Image'}`
                              )}
                            >
                              {item.output_format === 'video' ? (
                                <SecureVideo
                                  src={getSecureAdminAITestUrl(item.generated_video_path, item.id)}
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'cover',
                                  }}
                                  controls
                                />
                              ) : (
                                <SecureImage
                                  src={getSecureAdminAITestUrl(item.generated_image_path, item.id)}
                                  alt="Output"
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'cover',
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>

                      {/* Processing Status with Progress Bar */}
                      {item.status === 'PROCESSING' && (
                        <Box sx={{ mt: 2 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography 
                              variant="subtitle2" 
                              sx={{ 
                                fontWeight: 600,
                                color: 'primary.main',
                                fontSize: '0.875rem'
                              }}
                            >
                              Processing...
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ fontSize: '0.75rem' }}
                            >
                              {item.processing_time_seconds ? `${item.processing_time_seconds}s` : 'In progress'}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: '100%',
                              height: 6,
                              backgroundColor: 'grey.200',
                              borderRadius: 3,
                              overflow: 'hidden'
                            }}
                          >
                            <Box
                              sx={{
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%)',
                                backgroundSize: '200% 100%',
                                animation: 'progress 2s ease-in-out infinite',
                                '@keyframes progress': {
                                  '0%': {
                                    backgroundPosition: '200% 0'
                                  },
                                  '100%': {
                                    backgroundPosition: '-200% 0'
                                  }
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      )}

                      {/* Pending Status */}
                      {item.status === 'PENDING' && (
                        <Box sx={{ mt: 2 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography 
                              variant="subtitle2" 
                              sx={{ 
                                fontWeight: 600,
                                color: 'warning.main',
                                fontSize: '0.875rem'
                              }}
                            >
                              Queued for Processing
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ fontSize: '0.75rem' }}
                            >
                              Waiting...
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: '100%',
                              height: 6,
                              backgroundColor: 'grey.200',
                              borderRadius: 3,
                              overflow: 'hidden'
                            }}
                          >
                            <Box
                              sx={{
                                width: '30%',
                                height: '100%',
                                backgroundColor: 'warning.main',
                                borderRadius: 3
                              }}
                            />
                          </Box>
                        </Box>
                      )}

                      {/* Error Message */}
                      {item.error_message && (
                        <Alert 
                          severity="error" 
                          sx={{ 
                            mb: 2,
                            borderRadius: 2,
                            fontSize: '0.75rem'
                          }}
                        >
                          <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                            {item.error_message}
                          </Typography>
                        </Alert>
                      )}

                      {/* Footer */}
                      <Box 
                        mt="auto" 
                        display="flex" 
                        justifyContent="space-between" 
                        alignItems="center"
                        sx={{ pt: 2, borderTop: '1px solid', borderColor: 'grey.100' }}
                      >
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: '0.75rem' }}
                        >
                          {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<ViewIcon />}
                          onClick={() => handleViewDetails(item)}
                          size="small"
                          sx={{
                            borderRadius: 2,
                            px: 2,
                            py: 0.5,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textTransform: 'none'
                          }}
                        >
                          View Details
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Image Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={handleCloseImageDialog}
        maxWidth="md"
        fullWidth
        sx={{ zIndex: 10000 }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {selectedImage?.title}
            <IconButton onClick={handleCloseImageDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 400,
                textAlign: 'center'
              }}
            >
              {selectedImage.url.includes('/api/admin/ai-test/preview/') ? (
                <SecureImage
                  src={selectedImage.url}
                  alt={selectedImage.title}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    objectFit: 'contain',
                    display: 'block',
                    margin: '0 auto'
                  }}
                />
              ) : (
                <img
                  src={selectedImage.url}
                  alt={selectedImage.title}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    objectFit: 'contain',
                    display: 'block',
                    margin: '0 auto'
                  }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImageDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        maxWidth="xl"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: 'grey.50',
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            py: 2
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" component="h2" gutterBottom>
                Generation Details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedHistoryItem?.batch_id || `Generation #${selectedHistoryItem?.id}`}
              </Typography>
            </Box>
            <IconButton 
              onClick={handleCloseDetailsDialog}
              sx={{
                backgroundColor: 'grey.100',
                '&:hover': {
                  backgroundColor: 'grey.200'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedHistoryItem && (
            <Box>
              {/* Status Header */}
              <Box 
                sx={{ 
                  p: 3, 
                  backgroundColor: 'grey.50',
                  borderBottom: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
                  <MuiChip
                    label={selectedHistoryItem.status}
                    color={getStatusColor(selectedHistoryItem.status)}
                    icon={selectedHistoryItem.status === 'COMPLETED' ? <CompletedIcon /> : <ErrorIcon />}
                    sx={{
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '0.875rem'
                    }}
                  />
                  <Chip
                    icon={<FormatIcon />}
                    label={selectedHistoryItem.output_format?.toUpperCase() || 'UNKNOWN'}
                    variant="outlined"
                    color="primary"
                    size="small"
                  />
                  {selectedHistoryItem.processing_time_seconds && (
                    <Chip
                      icon={<TimeIcon />}
                      label={`${selectedHistoryItem.processing_time_seconds}s`}
                      variant="outlined"
                      color="secondary"
                      size="small"
                    />
                  )}
                </Box>

                {/* Real-time Progress Bar for Processing Status */}
                {selectedHistoryItem.status === 'PROCESSING' && (
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: 600,
                          color: 'primary.main'
                        }}
                      >
                        Processing in Progress...
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                      >
                        {selectedHistoryItem.processing_time_seconds ? `${selectedHistoryItem.processing_time_seconds}s elapsed` : 'In progress'}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        height: 8,
                        backgroundColor: 'grey.200',
                        borderRadius: 4,
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%)',
                          backgroundSize: '200% 100%',
                          animation: 'progress 2s ease-in-out infinite',
                          '@keyframes progress': {
                            '0%': {
                              backgroundPosition: '200% 0'
                            },
                            '100%': {
                              backgroundPosition: '-200% 0'
                            }
                          }
                        }}
                      />
                    </Box>
                  </Box>
                )}

                {/* Pending Status Progress */}
                {selectedHistoryItem.status === 'PENDING' && (
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: 600,
                          color: 'warning.main'
                        }}
                      >
                        Queued for Processing
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                      >
                        Waiting in queue...
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        height: 8,
                        backgroundColor: 'grey.200',
                        borderRadius: 4,
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          width: '30%',
                          height: '100%',
                          backgroundColor: 'warning.main',
                          borderRadius: 4
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Images Section */}
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  {/* Input Image */}
                  <Grid item xs={12} md={6}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        borderRadius: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    >
                      <CardContent>
                        <Typography 
                          variant="h6" 
                          gutterBottom
                          sx={{ 
                            fontWeight: 600,
                            color: 'text.primary',
                            mb: 2
                          }}
                        >
                          Input Image
                        </Typography>
                        {selectedHistoryItem.original_image_path ? (
                          <Box
                            sx={{
                              width: '100%',
                              height: 400,
                              border: '2px solid',
                              borderColor: 'grey.200',
                              borderRadius: 2,
                              overflow: 'hidden',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'grey.50',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: 'primary.50'
                              }
                            }}
                            onClick={() => handleImageClick(selectedHistoryItem.original_image_path, 'Input Image')}
                          >
                            <img
                              src={selectedHistoryItem.original_image_path}
                              alt="Input"
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                              }}
                            />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: 400,
                              border: '2px dashed',
                              borderColor: 'grey.300',
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'grey.50',
                            }}
                          >
                            <Box textAlign="center">
                              <ImageIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                              <Typography color="text.secondary">No input image</Typography>
                            </Box>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Output Image/Video */}
                  <Grid item xs={12} md={6}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        borderRadius: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    >
                      <CardContent>
                        <Typography 
                          variant="h6" 
                          gutterBottom
                          sx={{ 
                            fontWeight: 600,
                            color: 'text.primary',
                            mb: 2
                          }}
                        >
                          Output {selectedHistoryItem.output_format === 'video' ? 'Video' : 'Image'}
                        </Typography>
                        {(selectedHistoryItem.generated_image_path || selectedHistoryItem.generated_video_path) ? (
                          <Box
                            sx={{
                              width: '100%',
                              height: 400,
                              border: '2px solid',
                              borderColor: 'success.200',
                              borderRadius: 2,
                              overflow: 'hidden',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'success.50',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                borderColor: 'success.main',
                                backgroundColor: 'success.100'
                              }
                            }}
                            onClick={() => handleImageClick(
                              getSecureAdminAITestUrl(selectedHistoryItem.generated_image_path || selectedHistoryItem.generated_video_path, selectedHistoryItem.id, false),
                              `Output ${selectedHistoryItem.output_format === 'video' ? 'Video' : 'Image'}`
                            )}
                          >
                            {selectedHistoryItem.output_format === 'video' ? (
                              <SecureVideo
                                src={getSecureAdminAITestUrl(selectedHistoryItem.generated_video_path, selectedHistoryItem.id)}
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  objectFit: 'contain',
                                }}
                                controls
                              />
                            ) : (
                              <SecureImage
                                src={getSecureAdminAITestUrl(selectedHistoryItem.generated_image_path, selectedHistoryItem.id)}
                                alt="Output"
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  objectFit: 'contain',
                                }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: 400,
                              border: '2px dashed',
                              borderColor: 'grey.300',
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'grey.50',
                            }}
                          >
                            <Box textAlign="center">
                              <ImageIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                              <Typography color="text.secondary">No output generated</Typography>
                            </Box>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>

              {/* Details Section */}
              <Box sx={{ p: 3, backgroundColor: 'grey.50' }}>
                <Card 
                  sx={{ 
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                >
                  <CardContent>
                    <Typography 
                      variant="h6" 
                      gutterBottom
                      sx={{ 
                        fontWeight: 600,
                        color: 'text.primary',
                        mb: 3
                      }}
                    >
                      Generation Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 2 }}>
                          <Box display="flex" alignItems="center" mb={1}>
                            <FormatIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="subtitle2" fontWeight={600}>
                              Format
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {selectedHistoryItem.output_format || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 2 }}>
                          <Box display="flex" alignItems="center" mb={1}>
                            <TimeIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="subtitle2" fontWeight={600}>
                              Created
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {selectedHistoryItem.created_at ? new Date(selectedHistoryItem.created_at).toLocaleString() : 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 2 }}>
                          <Box display="flex" alignItems="center" mb={1}>
                            <CompletedIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="subtitle2" fontWeight={600}>
                              Completed
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {selectedHistoryItem.completed_at ? new Date(selectedHistoryItem.completed_at).toLocaleString() : 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>

                      {selectedHistoryItem.processing_time_seconds && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 2 }}>
                            <Box display="flex" alignItems="center" mb={1}>
                              <TimeIcon sx={{ mr: 1, color: 'primary.main' }} />
                              <Typography variant="subtitle2" fontWeight={600}>
                                Processing Time
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {selectedHistoryItem.processing_time_seconds} seconds
                            </Typography>
                          </Box>
                        </Grid>
                      )}

                      {selectedHistoryItem.batch_id && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 2 }}>
                            <Box display="flex" alignItems="center" mb={1}>
                              <FormatIcon sx={{ mr: 1, color: 'primary.main' }} />
                              <Typography variant="subtitle2" fontWeight={600}>
                                Batch ID
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {selectedHistoryItem.batch_id}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>

                    {/* Prompt Section */}
                    {selectedHistoryItem.prompt && (
                      <Box sx={{ mt: 3 }}>
                        <Typography 
                          variant="subtitle1" 
                          fontWeight={600}
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          <PromptIcon sx={{ mr: 1, color: 'primary.main' }} />
                          Prompt
                        </Typography>
                        <Box 
                          sx={{ 
                            p: 2, 
                            backgroundColor: 'white', 
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'grey.200'
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {selectedHistoryItem.prompt}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Error Message */}
                    {selectedHistoryItem.error_message && (
                      <Box sx={{ mt: 3 }}>
                        <Alert 
                          severity="error" 
                          sx={{ 
                            borderRadius: 2,
                            '& .MuiAlert-message': {
                              width: '100%'
                            }
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            Error Details
                          </Typography>
                          <Typography variant="body2">
                            {selectedHistoryItem.error_message}
                          </Typography>
                        </Alert>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions 
          sx={{ 
            p: 3, 
            backgroundColor: 'grey.50',
            borderTop: '1px solid',
            borderColor: 'grey.200'
          }}
        >
          <Button 
            onClick={handleCloseDetailsDialog}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestModule; 